export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const LEVEL_RANK: Readonly<Record<LogLevel, number>> = { debug: 10, info: 20, warn: 30, error: 40 };

export interface Logger {
  debug(message: string, meta?: unknown): void;
  info(message: string, meta?: unknown): void;
  warn(message: string, meta?: unknown): void;
  error(message: string, meta?: unknown): void;
}

/** Where every logger's lines go. */
export interface LogDestination {
  write(level: LogLevel, line: string): void;
  /** Resolves once every line written is where it was going. */
  close(): Promise<void>;
}

export class ConsoleLogDestination implements LogDestination {
  write(_level: LogLevel, line: string): void {
    console.log(line);
  }

  close(): Promise<void> {
    return Promise.resolve();
  }
}

let destination: LogDestination = new ConsoleLogDestination();

function formatMeta(meta: unknown): string {
  if (meta === undefined) {
    return '';
  }
  if (meta instanceof Error) {
    return ` ${meta.stack ?? `${meta.name}: ${meta.message}`}`;
  }
  if (typeof meta === 'string') {
    return ` ${meta}`;
  }
  try {
    return ` ${JSON.stringify(meta)}`;
  } catch {
    return ' [unserializable meta]';
  }
}

class LoggerImpl implements Logger {
  constructor(private readonly name: string) {}

  private write(level: LogLevel, message: string, meta?: unknown): void {
    if (LEVEL_RANK[level] < LEVEL_RANK[LoggerFactory.level]) {
      return;
    }
    destination.write(level, `${new Date().toISOString()} ${level.toUpperCase().padEnd(5)} [${this.name}] ${message}${formatMeta(meta)}`);
  }

  debug(message: string, meta?: unknown): void {
    this.write('debug', message, meta);
  }
  info(message: string, meta?: unknown): void {
    this.write('info', message, meta);
  }
  warn(message: string, meta?: unknown): void {
    this.write('warn', message, meta);
  }
  error(message: string, meta?: unknown): void {
    this.write('error', message, meta);
  }
}

/**
 * Loggers are named after the class or module that owns them, which is what makes
 * the output greppable when the API, injector and corporate-action job are all writing at once.
 */
export class LoggerFactory {
  static level: LogLevel = 'info';
  private static readonly loggers = new Map<string, Logger>();

  static getLogger(name: string): Logger {
    const existing = LoggerFactory.loggers.get(name);
    if (existing !== undefined) {
      return existing;
    }
    const logger = new LoggerImpl(name);
    LoggerFactory.loggers.set(name, logger);
    return logger;
  }

  static setLevel(level: LogLevel): void {
    LoggerFactory.level = level;
  }

  /** Sends every logger's lines to `next` from now on, loggers already made included, and closes the one it replaces. */
  static async setDestination(next: LogDestination): Promise<void> {
    const replaced = destination;
    destination = next;
    await replaced.close();
  }

  /** Closes the destination and goes back to the console, so a line logged after it still shows. Call before the process exits. */
  static async close(): Promise<void> {
    await LoggerFactory.setDestination(new ConsoleLogDestination());
  }
}
